import {
  ALL_VC_TYPE_NAME,
  VI_TYPE,
  VC_TYPE,
  CUSTOM_AMOUNT,
  BUNDLE_TYPE,
  GK_TYPE,
} from '../../../utils/store-helper';
import { changeSelectedQuantity } from '../../../utils/store/select-quantity';
import { updateHidingButtons } from '../../../utils/store/update-hiding-buttons';
import { toggleElement } from '../../helpers/common';
import { getStore } from '../../store';
import { translations } from '../../translations';
import { UniversalCart } from './cart/UniversalCart';
import { renderCustomAmount } from './render-custom-amount';
import { unmountSectionPreloader } from './section-preloader-render';
import {
  IStoreProps,
  collectGeneratedData,
  renderEmptyTemplate,
  renderStoreSection,
} from './section-render';
import { InventoryService } from './services/InventoryService';

const canPurchase = (sku, inventory) => {
  const inventoryItem = inventory.getItem(sku);
  return (
    !inventoryItem ||
    inventoryItem.virtualItemType !== 'non_consumable' ||
    inventoryItem.quantity === 0
  );
};

const addListeners = ({
  templateDataList,
  cart,
  inventory,
  userToken,
}: {
  templateDataList: IStoreProps[];
  cart: UniversalCart;
  inventory: InventoryService;
  userToken: string;
}) => {
  const cartModal = document.querySelector('[data-cart-modal]');
  templateDataList.forEach((templateData) => {
    const buyButtons = document.querySelectorAll(
      `#${templateData.buttonId}, #${templateData.buttonId}--modal`
    );
    buyButtons.forEach((buyButton: Element) => {
      if (cartModal && templateData.enableCart) {
        // $FlowFixMe
        const parentElement = buyButton.parentNode;
        const addToCartButton =
          parentElement &&
          parentElement.querySelector('[data-button-type="add-to-cart"]');
        const checkoutButton =
          parentElement &&
          parentElement.querySelector('[data-button-type="cart-modal-opener"]');
        const cartSelectButton =
          parentElement &&
          parentElement.querySelector('[data-button-type="cart-select"]');
        if (addToCartButton || checkoutButton) {
          const updateButtonsProps = {
            addToCartButton,
            cartSelectButton,
            buyButton,
            checkoutButton,
            cart,
            sku: templateData.itemSku,
            storeAPIDataService: templateData.storeAPIDataService,
          };
          cart.addListener(() => updateHidingButtons(updateButtonsProps));
          updateHidingButtons(updateButtonsProps);
          if (cartSelectButton) {
            cart.addListener(() => {
              changeSelectedQuantity(cart, templateData.itemSku);
            });
            changeSelectedQuantity(cart, templateData.itemSku);
          }
        }
      }
      if (userToken) {
        const footer = buyButton.closest('.store__item-footer');
        if (footer) {
          const storeButtons = footer.querySelector(
            '[data-type="store-buttons"]'
          );
          const priceBlock = footer.querySelector('[data-type="price-block"]');
          const purchasedText = footer.querySelector(
            '[data-type="purchased-text"]'
          );
          const showPurchasedText = () => {
            const hiddenPurchasedText = canPurchase(
              templateData.itemSku,
              inventory
            );
            toggleElement(storeButtons, () => hiddenPurchasedText);
            toggleElement(priceBlock, () => hiddenPurchasedText);
            toggleElement(purchasedText, () => !hiddenPurchasedText);
          };
          inventory.addListener(() => showPurchasedText());
        }
      }
    });
  });
};

export const computeCatalogButtons = ({
  bundles,
  gameKeys,
  virtualItems,
  virtualCurrencyPacks,
  virtualCurrencies,
  block,
  universalItemsSections,
  blockStructure,
  enableCart,
  userToken,
}) => {
  const store = getStore();
  const {
    scripts: { landingType, locale },
    landingServices: { cart, inventory, storeAPIDataService },
  } = store.getState();

  const sections = block.querySelectorAll('.store__content');
  const sectionLists = block.querySelectorAll('.store__list');
  const sectionTitles = block.querySelectorAll('.store__section-title');

  const buttons = universalItemsSections.reduce(
    (
      universalItemsSectionsButtons,
      { id: groupId, sectionIndex, type: itemSectionType }
    ) => {
      const currentSection = sections[sectionIndex];
      const currentSectionList = sectionLists[sectionIndex];
      const currentSectionTitle = sectionTitles[sectionIndex];
      let currentGroupItems: any = [];
      if (itemSectionType === BUNDLE_TYPE) {
        currentGroupItems = bundles;
      }
      if (itemSectionType === GK_TYPE) {
        currentGroupItems = gameKeys;
      }
      if (itemSectionType === VI_TYPE) {
        const foundVI = virtualItems.find((vi) => vi.groupId === groupId);
        currentGroupItems = foundVI && foundVI.data;
      }
      const hasVCPacksError =
        // $FlowFixMe
        !!virtualCurrencyPacks?.errorMessage ||
        // $FlowFixMe
        !!virtualCurrencies?.errorMessage;
      if (itemSectionType === VC_TYPE && !hasVCPacksError) {
        currentGroupItems =
          groupId === ALL_VC_TYPE_NAME
            ? virtualCurrencyPacks
            : virtualCurrencyPacks.filter(
                (pack) => pack.content[0].sku === groupId
              );
      }

      if (itemSectionType === CUSTOM_AMOUNT && !hasVCPacksError) {
        currentGroupItems = virtualCurrencies.find(
          ({ sku }) => sku === groupId
        );
        if (!currentGroupItems) {
          return universalItemsSectionsButtons;
        }
        renderCustomAmount({
          currentGroupItems,
          currentSection,
          currentSectionList,
          blockId: block.id,
        });
      }

      if (groupId !== ALL_VC_TYPE_NAME) {
        const groupTitle =
          itemSectionType === VI_TYPE
            ? Array.isArray(currentGroupItems) &&
              currentGroupItems.length > 0 &&
              currentGroupItems[0].groups.find(
                (element) => element.external_id === groupId.toString()
              )
            : virtualCurrencies.find(({ sku }) => sku === groupId);
        if (groupTitle && currentSectionTitle) {
          currentSectionTitle.innerText = groupTitle.name;
        }
      }
      if (currentGroupItems && Array.isArray(currentGroupItems)) {
        currentGroupItems = currentGroupItems.filter((item) => item.canShow());
      }

      const isEmptyItems =
        currentGroupItems &&
        (Array.isArray(currentGroupItems)
          ? currentGroupItems.length === 0
          : currentGroupItems.statusCode === 404);
      if (isEmptyItems) {
        unmountSectionPreloader(currentSection);
        renderEmptyTemplate(currentSectionList, {
          isEmptyList: true,
          emptyListText: translations['client.store.no_items'],
        });
        return universalItemsSectionsButtons;
      }

      let templateDataList: IStoreProps[] = [];
      if (Array.isArray(currentGroupItems)) {
        templateDataList = currentGroupItems.map((item) => {
          return collectGeneratedData({
            item,
            itemSectionType,
            groupId,
            sectionIndex,
            blockStructure,
            landingType,
            enableCart,
            cart,
            storeAPIDataService,
            block,
            locale,
          });
        });

        renderStoreSection({
          templateDataList,
          currentSection,
          currentSectionList,
        });
        addListeners({ templateDataList, cart, inventory, userToken });
      }

      const buttonsFromTemplateList: Element[] = [];
      templateDataList.forEach((templateData) => {
        const selector = `#${templateData.buttonId}, #${templateData.buttonId}--modal`;
        buttonsFromTemplateList.push(...document.querySelectorAll(selector));
      });

      return [...universalItemsSectionsButtons, ...buttonsFromTemplateList];
    },
    []
  );

  return buttons;
};
